import React, { useState, createContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import tmdbService from '../../services/tmdb';
import { buildPlayerUrl } from '../../services/players';
import { useAuth } from '../../context/Auth.context';
import {
  isCloudAvailable,
  readCloudData,
  saveLists,
  saveContinueWatching,
} from '../../services/cloudSync';
import { sendToTv, probeTv } from '../../services/cast';
import { getStoredPlayerId } from '../../services/players';
import CastModal from '../common/CastModal';

export const MovieContext = createContext();

// Player URL construction lives in services/players.js so the watch page and the
// tests share one registry (Player 1 = EmbedMaster, Player 2 = vidy.st).
export const buildEmbedUrl = (item, options = {}) => buildPlayerUrl(item, options);

// In-app route for the watch player — single source used by openWatch AND
// the TV cast channel (phone sends this path; TV navigates to it).
export const buildWatchPath = (item, { mediaType, season = 1, episode = 1 } = {}) => {
  if (!item || !item.id) return null;
  const isSeries = mediaType === 'tv' || isSeriesItem(item);
  return isSeries
    ? `/watch/tv/${item.id}/${season ?? 1}/${episode ?? 1}`
    : `/watch/movie/${item.id}`;
};

const isSeriesItem = (item) => Boolean(item?.media_type === 'tv' || item?.original_name || item?.name);

const MovieProvider = ({ children }) => {
  const [movie, setMovie] = useState({
    id: 0,
    original_title: '',
    title: '',
    overview: '',
    backdrop_path: '',
    poster_path: '',
    vote_average: 0,
    genres: [],
    runtime: 0,
  });

  const [search, setSearch] = useState('');

  // CAST — "Watch on TV" (phone -> TV over public MQTT, zero backend)
  const [castOpen, setCastOpen] = useState(false);
  const [castItem, setCastItem] = useState(null);
  const [castOptions, setCastOptions] = useState(null);
  const [tvCode, setTvCode] = useState(() => {
    try {
      return localStorage.getItem('flexwatch_tvcode') || '';
    } catch (e) {
      return '';
    }
  });

  const saveTvCode = useCallback((code) => {
    setTvCode(code || '');
    try {
      if (code) localStorage.setItem('flexwatch_tvcode', code);
      else localStorage.removeItem('flexwatch_tvcode');
    } catch (e) {
      /* private mode */
    }
  }, []);

  // ----- myList: local state, hydrated from localStorage, synced to cloud -----
  const [myList, setMyList] = useState(() => {
    try {
      const saved = localStorage.getItem('flexwatch_mylist');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // -- continueWatching: {id, media_type, title, poster_path, backdrop_path,
  //    season, episode, seconds, duration, updatedAt} --
  const [continueWatching, setContinueWatching] = useState(() => {
    try {
      const saved = localStorage.getItem('flexwatch_continue');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [activeTrailer, setActiveTrailer] = useState(null); // { ...movie, videoKey, loading } or null
  const { isSignedIn } = useAuth();
  const syncTimer = useRef(null);
  const cwTimer = useRef(null);
  const signedInRef = useRef(isSignedIn);
  signedInRef.current = isSignedIn;

  const navigate = useNavigate();

  // ==========================================================================
  // CLOUD SYNC
  // ==========================================================================
  // One-shot pull on sign-in: cloud lists win (they are the account's truth),
  // then merged with local items so nothing the user added offline is lost.
  useEffect(() => {
    let cancelled = false;
    const pull = async () => {
      if (!isSignedIn || !isCloudAvailable()) return;
      try {
        const cloud = await readCloudData();
        if (!cloud || cancelled) return;
        const cloudList = Array.isArray(cloud.lists) ? cloud.lists : [];
        if (cloudList.length > 0) {
          setMyList((prev) => {
            const merged = [...cloudList];
            prev.forEach((p) => {
              if (!merged.some((c) => String(c.id) === String(p.id))) merged.push(p);
            });
            try {
              localStorage.setItem('flexwatch_mylist', JSON.stringify(merged));
            } catch (e) {
              /* ignore */
            }
            return merged;
          });
        }
        const cloudCw = Array.isArray(cloud.continueWatching) ? cloud.continueWatching : [];
        if (cloudCw.length > 0) {
          setContinueWatching((prev) => {
            const merged = [...cloudCw];
            prev.forEach((p) => {
              if (!merged.some((c) => String(c.id) === String(p.id) && c.media_type === p.media_type)) {
                merged.push(p);
              }
            });
            try {
              localStorage.setItem('flexwatch_continue', JSON.stringify(merged));
            } catch (e) {
              /* ignore */
            }
            return merged;
          });
        }
      } catch (e) {
        /* offline / no cloud — local storage stays authoritative */
      }
    };
    pull();
    return () => {
      cancelled = true;
    };
  }, [isSignedIn]);

  // Debounced push of watchlist changes to cloud (when cloud is available).
  useEffect(() => {
    try {
      localStorage.setItem('flexwatch_mylist', JSON.stringify(myList));
    } catch (e) {
      /* ignore */
    }
    if (!isCloudAvailable() || !signedInRef.current) return undefined;
    clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      saveLists(myList).catch(() => {});
    }, 1500);
    return () => clearTimeout(syncTimer.current);
  }, [myList]);

  // Debounced push of continue-watching changes to cloud.
  useEffect(() => {
    try {
      localStorage.setItem('flexwatch_continue', JSON.stringify(continueWatching));
    } catch (e) {
      /* ignore */
    }
    if (!isCloudAvailable() || !signedInRef.current) return undefined;
    clearTimeout(cwTimer.current);
    cwTimer.current = setTimeout(() => {
      saveContinueWatching(continueWatching).catch(() => {});
    }, 1500);
    return () => clearTimeout(cwTimer.current);
  }, [continueWatching]);

  // ==========================================================================
  // ACTIONS
  // ==========================================================================
  const toggleMyList = useCallback(
    (movieItem) => {
      if (!movieItem || !movieItem.id) return;
      const exists = myList.some((m) => String(m.id) === String(movieItem.id));
      setMyList((prev) => {
        if (exists) {
          return prev.filter((m) => String(m.id) !== String(movieItem.id));
        }
        return [...prev, movieItem];
      });
    },
    [myList]
  );

  const isInMyList = useCallback(
    (movieId) => myList.some((m) => String(m.id) === String(movieId)),
    [myList]
  );

  // Dynamically fetch the official movie trailer from TMDB videos
  const openTrailer = useCallback(async (movieItem) => {
    if (!movieItem) return;

    // Open modal immediately with loading state
    setActiveTrailer({
      ...movieItem,
      videoKey: null,
      loading: true,
    });

    try {
      if (movieItem.id) {
        const videos = isSeriesItem(movieItem)
          ? await tmdbService.getTVVideos(movieItem.id)
          : await tmdbService.getMovieVideos(movieItem.id);
        // Priority: Official YouTube Trailer > YouTube Trailer > YouTube Teaser > Any YouTube Clip
        const trailer =
          videos.find((v) => v.site === 'YouTube' && v.type === 'Trailer' && v.official) ||
          videos.find((v) => v.site === 'YouTube' && v.type === 'Trailer') ||
          videos.find((v) => v.site === 'YouTube' && v.type === 'Teaser') ||
          videos.find((v) => v.site === 'YouTube' && v.type === 'Clip') ||
          videos.find((v) => v.site === 'YouTube');

        setActiveTrailer({
          ...movieItem,
          videoKey: trailer ? trailer.key : null,
          loading: false,
        });
      } else {
        setActiveTrailer({
          ...movieItem,
          videoKey: null,
          loading: false,
        });
      }
    } catch (err) {
      console.error('Error fetching trailer videos from TMDB:', err);
      setActiveTrailer({
        ...movieItem,
        videoKey: null,
        loading: false,
      });
    }
  }, []);

  const closeTrailer = useCallback(() => setActiveTrailer(null), []);

  // Open the player: when a TV is paired, TV plays and phone opens Remote;
  // when no TV is connected, fall back to the normal in-app local player.
  const openWatch = useCallback(
    (item, watchOptions = {}) => {
      if (!item || !item.id) return;
      const mediaType = watchOptions.mediaType || (isSeriesItem(item) ? 'tv' : 'movie');
      const season = Number(watchOptions.season) || 1;
      const episode = Number(watchOptions.episode) || 1;
      const path = buildWatchPath(item, { mediaType, season, episode });
      const code = String(tvCode || '').replace(/\D/g, '');

      if (code.length === 6) {
        probeTv(code)
          .then((online) => {
            if (!online) {
              navigate(path);
              return null;
            }
            const title = item.title || item.original_title || item.name || item.original_name || '';
            return sendToTv(code, {
              type: 'play',
              path,
              title,
              poster_path: item.poster_path || null,
              backdrop_path: item.backdrop_path || null,
              season: mediaType === 'tv' ? season : null,
              episode: mediaType === 'tv' ? episode : null,
              player: getStoredPlayerId(),
            }).then((result) => {
              if (result && result.ok) {
                saveTvCode(code);
                navigate('/remote');
              } else {
                navigate(path);
              }
            });
          })
          .catch(() => navigate(path));
        return;
      }

      navigate(path);
    },
    [navigate, tvCode, saveTvCode]
  );

  // Record/update a continue-watching entry (called by the Watch player page).
  const upsertContinueWatching = useCallback((entry) => {
    if (!entry || !entry.id) return;
    setContinueWatching((prev) => {
      const rest = prev.filter(
        (x) => !(String(x.id) === String(entry.id) && x.media_type === entry.media_type)
      );
      return [entry, ...rest].slice(0, 20);
    });
  }, []);

  const removeFromContinueWatching = useCallback((id, mediaType) => {
    setContinueWatching((prev) =>
      prev.filter((x) => !(String(x.id) === String(id) && x.media_type === mediaType))
    );
  }, []);

  const openCast = useCallback((item, watchOptions = {}) => {
    if (!item || !item.id) return;
    setCastItem(item);
    setCastOptions(watchOptions);
    setCastOpen(true);
  }, []);

  const closeCast = useCallback(() => setCastOpen(false), []);

  const castPath = useMemo(
    () => (castItem ? buildWatchPath(castItem, castOptions || {}) : null),
    [castItem, castOptions]
  );

  const value = useMemo(
    () => ({
      movie,
      setMovie,
      search,
      setSearch,
      myList,
      toggleMyList,
      isInMyList,
      activeTrailer,
      openTrailer,
      closeTrailer,
      openWatch,
      continueWatching,
      upsertContinueWatching,
      removeFromContinueWatching,
      castCode: tvCode,
      saveTvCode,
      openCast,
      closeCast,
    }),
    [
      movie,
      search,
      myList,
      toggleMyList,
      isInMyList,
      activeTrailer,
      openTrailer,
      closeTrailer,
      openWatch,
      continueWatching,
      upsertContinueWatching,
      removeFromContinueWatching,
      tvCode,
      saveTvCode,
      openCast,
      closeCast,
    ]
  );

  return (
    <MovieContext.Provider value={value}>
      {children}
      <CastModal
        open={castOpen}
        item={castItem}
        watchOptions={castOptions ? { ...castOptions, path: castPath } : null}
        tvCode={tvCode}
        onSaveCode={saveTvCode}
        onClose={closeCast}
      />
    </MovieContext.Provider>
  );
};

export default MovieProvider;
